<template>
    <div class="relative flex">
        <div class="flex flex-row gap-[0.0625rem]">
            <!-- Inserted before parts -->
            <AllocationPart
                v-for="(part, partIndex) in insertedPhasesBefore"
                :key="`${part.form}-${partIndex}`"
                :color="props.color"
                :form="part.form"
                :is-phase-end="partIndex === props.cellSpanBefore && props.confirmedCellSpan === 0"
                :variant="props.isAllocationOptional ? 'Light-Optional' : 'Light-Mandatory'"
                :specialization="null"
                :has-temporary-change-marker="false"
            />

            <!-- Confirmed parts -->
            <AllocationPart
                v-for="(part, partIndex) in confirmedAllocationParts"
                :key="`${part.form}-${partIndex}`"
                :color="props.color"
                :form="part.form"
                :is-phase-end="partIndex === props.confirmedCellSpan - 1 && props.cellSpanAfter <= 0"
                :variant="mapVariant(part.variant, props.isAllocationOptional)"
                :specialization="props.selectedSpecialization"
                :has-temporary-change-marker="props.hasTemporaryChangeMarker"
            />

            <!-- Inserted after parts -->
            <AllocationPart
                v-for="(part, partIndex) in insertedPhasesAfter"
                :key="`${part.form}-${part}`"
                :color="props.color"
                :form="part.form"
                :is-phase-end="partIndex === props.cellSpanAfter - 1"
                :variant="props.isAllocationOptional ? 'Light-Optional' : 'Light-Mandatory'"
                :specialization="null"
                :has-temporary-change-marker="false"
            />
        </div>

        <InsertPhaseButton
            class="absolute -left-[6.5px] z-50"
            :style="insertLeftPhaseButtonTopStyle"
            :bar-height="props.isLeftEndPosition ? 'Full' : 'Small'"
            :phase-name="props.insertPhaseBefore"
            @click="$emit('insertPhaseBefore')"
        />

        <!-- Wrapper for confirmed phases -->
        <div class="absolute top-0 bottom-0" :style="{ ...wrapperConfirmedLeftStyle, ...wrapperConfirmedRightStyle }">
            <EditPhaseWidget
                v-if="props.position !== 'After-Main'"
                class="absolute bottom-0"
                :class="[leftClass]"
                :phase-type="props.type"
                :phase-name="props.phaseName"
                :phase-color="props.color"
                :direction="directionLeftEditWidget"
                :is-main-phase="props.position === 'Main'"
                :is-decreasing-deleting="
                    props.cellSpanBefore + props.confirmedCellSpan + props.cellSpanAfter <= 1 &&
                    props.position !== 'Main'
                "
                :is-decrease-button-disabled="
                    props.cellSpanBefore + props.confirmedCellSpan + props.cellSpanAfter === 0
                "
                :cell-span-before="props.cellSpanBefore"
                :cell-span-after="props.cellSpanAfter"
                :change-phase-status="props.isChangeTypePopupOpen ? 'Close-Popup' : 'Open-Popup'"
                :allowed-specializations="props.allowedSpecializations"
                :selected-specialization="
                    ((props.position !== 'Main' || (props.position === 'Main' && props.confirmedCellSpan <= 1)) &&
                        props.selectedSpecialization) ||
                    null
                "
                :has-temporary-change-marker="
                    (props.position !== 'Main' || (props.position === 'Main' && props.confirmedCellSpan <= 1)) &&
                    props.hasTemporaryChangeMarker
                "
                :display-direction-icons="props.confirmedCellSpan >= 4"
                @decrease-phase-length="
                    (direction) =>
                        direction === 'Left' ? $emit('decreasePhaseLengthBefore') : $emit('decreasePhaseLengthAfter')
                "
                @increase-phase-length="
                    (direction) =>
                        direction === 'Left' ? $emit('increasePhaseLengthBefore') : $emit('increasePhaseLengthAfter')
                "
                @toggle-change-phase-type="
                    props.isChangeTypePopupOpen ? $emit('hide-change-type-popup') : $emit('show-change-type-popup')
                "
                @change-specialization="(specialization) => $emit('change-specialization', specialization)"
            />

            <ColumnCounter
                v-if="props.confirmedCellSpan > 2"
                class="absolute -top-[1.4375rem] z-10"
                :class="[columnCounterLeftClass]"
                :count="props.confirmedCellSpan"
                :phase-name="props.phaseName"
                :phase-color="props.color"
                :display-double-width="props.confirmedCellSpan >= 5"
                :display-long-label="props.confirmedCellSpan >= 5"
            />

            <EditPhaseWidget
                v-if="(props.confirmedCellSpan >= 2 && props.position === 'Main') || props.position === 'After-Main'"
                class="absolute -top-[1.4375rem]"
                :class="[rightClass]"
                :phase-type="props.type"
                :phase-name="props.phaseName"
                :phase-color="props.color"
                direction="Right"
                :is-main-phase="props.position === 'Main'"
                :is-decreasing-deleting="
                    props.cellSpanBefore + props.confirmedCellSpan + props.cellSpanAfter <= 1 &&
                    props.position === 'After-Main'
                "
                :is-decrease-button-disabled="
                    props.cellSpanBefore + props.confirmedCellSpan + props.cellSpanAfter === 0
                "
                :has-change-phase-button="props.confirmedCellSpan <= 1"
                :cell-span-before="props.cellSpanBefore"
                :cell-span-after="props.cellSpanAfter"
                change-phase-status="Dont-Show"
                :is-change-type-popup-open="false"
                :allowed-specializations="props.allowedSpecializations"
                :has-temporary-change-marker="
                    (props.position !== 'Main' || (props.position === 'Main' && props.confirmedCellSpan <= 1)) &&
                    props.hasTemporaryChangeMarker
                "
                :display-direction-icons="props.confirmedCellSpan >= 4"
                @decrease-phase-length="$emit('decreasePhaseLengthAfter')"
                @increase-phase-length="$emit('increasePhaseLengthAfter')"
            />

            <ChangePhaseButton
                class="absolute left-[50%] -translate-x-[50%]"
                :style="changePhaseButtonTopStyle"
                :phase-type="props.type"
                :phase-color="props.color"
                :change-phase-status="props.isChangeTypePopupOpen ? 'Close-Popup' : 'Open-Popup'"
                :is-change-type-popup-open="props.isChangeTypePopupOpen"
                :specializations="props.allowedSpecializations"
                :selected-specialization="props.selectedSpecialization"
                :has-temporary-change-marker="props.hasTemporaryChangeMarker"
                @toggle-change-phase-button="
                    props.isChangeTypePopupOpen ? $emit('hide-change-type-popup') : $emit('show-change-type-popup')
                "
                @change-specialization="(specialization) => $emit('change-specialization', specialization)"
            />
        </div>

        <InsertPhaseButton
            v-if="isRightEndPosition"
            class="absolute -right-[6.5px] z-50"
            :style="insertRightPhaseButtonTopStyle"
            :bar-height="props.isRightEndPosition ? 'Full' : 'Small'"
            :phase-name="props.insertPhaseAfter"
            @click="$emit('insertPhaseAfter')"
        />
    </div>
</template>

<script setup lang="ts">
import { computed, watchEffect } from 'vue';
import { PhaseColor, PhaseForm, PhaseTypeDatabase } from '../Timeline.types';
import AllocationPart from '../allocation/AllocationPart.vue';
import { SpecializationType } from '../allocation/SpecializationMappings';
import { calculateInsertedPhases } from '../allocation/allocationForm';
import ChangePhaseButton from '../edit-popup/ChangePhaseButton.vue';
import ColumnCounter from '../edit-popup/ColumnCounter.vue';
import EditPhaseWidget from '../edit-popup/EditPhaseWidget.vue';
import InsertPhaseButton from '../edit-popup/InsertPhaseButton.vue';
import { allocationHeightInRem, columnWidthInRem } from '../sharedStyles';
import { calculateDaysForConfirmedAllocationParts } from './logic';

type PhaseGroupProps = {
    type: PhaseTypeDatabase;
    phaseName: string;
    color: PhaseColor;
    cellSpanBefore: number; // Positive number ==> Insert cells, negative number ==> Remove cells
    confirmedCellSpan: number;
    cellSpanAfter: number; // Positive number ==> Insert cells, negative number ==> Remove cells
    isLeftEndPosition: boolean;
    isRightEndPosition: boolean;
    insertPhaseBefore: string | null; // string === Name of phase, null === Button is disabled
    insertPhaseAfter: string | null; // string === Name of phase, null === Button is disabled
    position: 'Before-Main' | 'Main' | 'After-Main';
    isChangeTypePopupOpen: boolean;
    allowedSpecializations: (SpecializationType | null)[];
    selectedSpecialization: SpecializationType | null;
    hasTemporaryChangeMarker: boolean;
    isAllocationOptional: boolean;
    form: PhaseForm;
    hasTemporaryPhasesBefore: boolean;
    hasTemporaryPhasesAfter: boolean;
};

const props = defineProps<PhaseGroupProps>();

defineEmits<{
    (e: 'insertPhaseBefore'): void;
    (e: 'insertPhaseAfter'): void;
    (e: 'decreasePhaseLengthBefore'): void;
    (e: 'decreasePhaseLengthAfter'): void;
    (e: 'increasePhaseLengthBefore'): void;
    (e: 'increasePhaseLengthAfter'): void;
    (e: 'show-change-type-popup'): void;
    (e: 'hide-change-type-popup'): void;
    (e: 'change-specialization', specialization: SpecializationType | null): void;
}>();

function validateProps(props: PhaseGroupProps) {
    if (Math.max(props.cellSpanBefore, props.confirmedCellSpan, props.cellSpanAfter, 0) < 1) {
        throw new Error(
            `Cell span must be >= 1, got cell span before: ${props.cellSpanBefore}, confirmed cell span: ${props.confirmedCellSpan}, cell span after: ${props.cellSpanAfter}`,
        );
    }
}

watchEffect(() => {
    validateProps(props);
});

const confirmedAllocationParts = computed(() =>
    calculateDaysForConfirmedAllocationParts(
        props.form,
        props.confirmedCellSpan,
        props.cellSpanBefore,
        props.cellSpanAfter,
        props.position,
        props.hasTemporaryPhasesBefore,
        props.hasTemporaryPhasesAfter,
    ),
);

const insertedPhasesBefore = computed(() =>
    calculateInsertedPhases(props.cellSpanBefore, props.isLeftEndPosition, false),
);
const insertedPhasesAfter = computed(() =>
    calculateInsertedPhases(props.cellSpanAfter, false, props.isRightEndPosition),
);

const directionLeftEditWidget = computed(() => {
    switch (props.position) {
        case 'Before-Main':
            return 'Left';

        case 'Main': {
            if (props.confirmedCellSpan === 1) {
                return 'Both';
            }

            return 'Left';
        }

        case 'After-Main':
            return 'Right';

        default: {
            throw new Error(`Unexpected position: ${props.position}`);
        }
    }
});

const leftClass = computed(() => {
    // Center widget for non-main, 2-day phases
    if (props.confirmedCellSpan === 2 && props.position !== 'Main') {
        return 'left-[50%] -translate-x-[50%]';
    }

    // Keep position where the edit phase widget initially appeared to avoid shifting when phase size increases/decreases
    if (props.confirmedCellSpan === 0) {
        return 'right-0';
    }

    return 'left-0';
});

const columnCounterLeftClass = computed(() => {
    return 'left-[50%] -translate-x-[50%]'; // Always centered
});

const rightClass = computed(() => {
    // Center widget for non-main, 2-day phases
    if (props.confirmedCellSpan === 2 && props.position !== 'Main') {
        return 'right-[50%] translate-x-[50%]';
    }

    // Keep position where the edit phase widget initially appeared to avoid shifting when phase size increases/decreases
    if (props.confirmedCellSpan === 0) {
        return 'left-0';
    }

    return 'right-0';
});

const wrapperConfirmedLeftStyle = computed(() => {
    if (props.cellSpanBefore > 0 && props.confirmedCellSpan > 0) {
        return { left: `${(columnWidthInRem + 0.0625) * props.cellSpanBefore}rem` };
    }

    return {
        left: 0,
    };
});

const wrapperConfirmedRightStyle = computed(() => {
    if (props.cellSpanAfter > 0 && props.confirmedCellSpan > 0) {
        return { right: `${(columnWidthInRem + 0.0625) * props.cellSpanAfter}rem` };
    }

    return {
        right: 0,
    };
});

const changePhaseButtonTopStyle = computed(() => {
    return {
        top: `${allocationHeightInRem}rem`,
    };
});

const insertLeftPhaseButtonTopStyle = computed(() => {
    return {
        top: `${props.isLeftEndPosition ? allocationHeightInRem / 2 : allocationHeightInRem}rem`,
    };
});

const insertRightPhaseButtonTopStyle = computed(() => {
    return {
        top: `${props.isRightEndPosition ? allocationHeightInRem / 2 : allocationHeightInRem}rem`,
    };
});

const mapVariant = (variant: 'Solid' | 'Deleted', isAllocationOptional: boolean) => {
    if (variant === 'Solid') {
        return isAllocationOptional ? 'Strong-Optional' : 'Strong-Mandatory';
    }

    return isAllocationOptional ? 'Light-Red-Border-Optional' : 'Light-Red-Border-Mandatory';
};
</script>
